import React, { useState } from 'react';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { PostsService } from '~/services/domain/PostsService';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { useQueryClient } from '@tanstack/react-query';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Post } from '~/models/Post';
import { decodeHTML } from 'entities';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';

export interface IEditPostProps extends IContextSheetContentProps {
  payload?: {
    post: Post;
  };
  onPostEdited?: (post: Post) => void;
}

export function EditPost({ onClose, onPostEdited, payload }: IEditPostProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useDelayedAutoFocus();

  // Extract post from payload and get the original text (removing "(Edited)" if present)
  const post = payload?.post;
  const rawText = post?.Text?.endsWith('(Edited)') 
    ? post.Text.slice(0, -9) // Remove "(Edited)" text
    : post?.Text || '';
  
  // Decode HTML entities before showing in the editor
  const originalText = decodeHTML(rawText);
  
  const [text, setText] = useState(originalText);

  const handleSubmit = async () => {
    if (!text.trim() || !post?.Id) return;

    setIsSubmitting(true);
    try {
      const updatedPost = await PostsService.editPost(post.Id, text);

      // Invalidate all post related queries to refresh feeds and member walls
      await queryClient.invalidateQueries({
        queryKey: ['posts'],
      });

      showSuccessToast(t('post.editSuccess'));
      onPostEdited?.(updatedPost);
      onClose?.();
    } catch (error) {
      console.warn('Failed to edit post:', error);
      showErrorToast(t('post.editError'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <Textarea className="flex-1">
        <TextareaInput
          ref={textareaRef}
          placeholder={t('post.placeholder')}
          value={text}
          onChangeText={setText}
          textAlignVertical="top"
          className={`m-0 ${
            text.length <= Post.CHARACTER_LIMIT_FOR_LARGE_FONT ? 'text-xl' : ''
          }`}
        />
      </Textarea>
      <HStack className="shrink-0 justify-end">
        <Button
          onPress={handleSubmit}
          disabled={isSubmitting || !text.trim() || text === originalText}
          className="disabled:bg-primary-200"
          action="primary"
          size="md">
          <ButtonText className={isSubmitting || !text.trim() || text === originalText ? 'text-white/50' : ''}>
            {t('common.update')}
          </ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}

EditPost.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
  adjustForWebKeyboard: true,
} satisfies ISheetConfig;
